import queryClient from 'api/queryClient'
import { WALLET_DETAILS_QUERY_KEY, WalletDetails } from 'api/useWalletDetails'
import { BsvWalletType } from 'wallets/wallets'
import { WALLET_BALANCE_EVENT } from 'state/channels/useUserChannel'

const BALANCE_WAIT_TIMEOUT_MS = 15000

export const getCachedWalletDetails = (walletType: BsvWalletType): WalletDetails | undefined => {
    return queryClient.getQueryData([WALLET_DETAILS_QUERY_KEY, walletType]) as
        | WalletDetails
        | undefined
}

export const waitForWalletBalance = async (
    walletType: BsvWalletType,
    timeoutMs: number = BALANCE_WAIT_TIMEOUT_MS,
): Promise<number | null> => {
    const existing = getCachedWalletDetails(walletType)
    if (existing?.balanceLoaded) {
        return existing.balance || 0
    }

    if (typeof window === 'undefined') {
        return null
    }

    return new Promise(resolve => {
        let timeoutId: number
        const unsubscribeFromCache = queryClient.getQueryCache().subscribe(event => {
            const queryKey = event?.query?.queryKey
            if (
                Array.isArray(queryKey) &&
                queryKey[0] === WALLET_DETAILS_QUERY_KEY &&
                queryKey[1] === walletType
            ) {
                const latest = getCachedWalletDetails(walletType)
                if (latest?.balanceLoaded) {
                    cleanup()
                    resolve(latest.balance || 0)
                }
            }
        })

        const cleanup = () => {
            window.removeEventListener(WALLET_BALANCE_EVENT, handleBalanceEvent)
            window.clearTimeout(timeoutId)
            unsubscribeFromCache()
        }

        const handleBalanceEvent = (event: Event) => {
            const balanceEvent = event as CustomEvent<{
                walletType: BsvWalletType
                balance: number
                balanceLoaded: boolean
            }>
            const detail = balanceEvent.detail
            if (detail?.walletType !== walletType) {
                return
            }
            cleanup()
            resolve(detail?.balance ?? 0)
        }

        timeoutId = window.setTimeout(() => {
            cleanup()
            const latest = getCachedWalletDetails(walletType)
            resolve(latest?.balanceLoaded ? latest.balance || 0 : null)
        }, timeoutMs)

        window.addEventListener(WALLET_BALANCE_EVENT, handleBalanceEvent)
    })
}
